import { useState, useRef, useEffect } from "react";

interface ChatMessage {
  id: string;
  from: "user" | "bot";
  text: string;
  time: string;
  showConsultOptions?: boolean;
}

const getTime = () => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
};

const AI_QA: { keywords: string[]; answer: string; offerConsult?: boolean }[] = [
  {
    keywords: ["설치", "시공", "달아", "달아줘", "설치해"],
    answer: "도어준은 서울·경기·인천 전 지역 당일 설치가 가능합니다.\n자격증 보유 전문 기사가 직접 방문해 시공해 드려요!\n\n바로 상담을 원하시면 아래에서 편한 방법을 선택해 주세요 😊",
    offerConsult: true,
  },
  {
    keywords: ["가격", "얼마", "비용", "견적"],
    answer: "제품 종류와 설치 환경에 따라 가격이 달라집니다.\n정확한 견적은 상담을 통해 안내해 드릴게요!\n\n아래에서 편한 상담 방법을 선택해 주세요 😊",
    offerConsult: true,
  },
  {
    keywords: ["브랜드", "제품", "추천", "어떤"],
    answer: "도어준은 삼성·게이트맨·아이레보·마스터즈·유니코 등 국내 주요 브랜드를 모두 취급합니다.\n용도와 예산에 맞는 제품을 추천해 드릴 수 있어요!",
  },
  {
    keywords: ["as", "a/s", "고장", "수리", "점검"],
    answer: "설치 후 AS도 도어준이 책임집니다.\n연중무휴 10:00~18:00 운영 중이니 언제든지 연락 주세요!\n\n빠른 상담을 원하시면 아래를 선택해 주세요 😊",
    offerConsult: true,
  },
  {
    keywords: ["시간", "운영", "영업", "언제"],
    answer: "도어준은 연중무휴 오전 10시 ~ 오후 6시 운영합니다.\n공휴일에도 상담 가능하니 편하게 연락 주세요!",
  },
  {
    keywords: ["위치", "어디", "지역", "서울", "경기", "인천"],
    answer: "서울·경기·인천 전 지역 방문 설치가 가능합니다.\n의정부 본사 기준으로 당일 출동도 가능해요!",
  },
  {
    keywords: ["카카오", "카톡", "채팅"],
    answer: "카카오 오픈채팅으로 바로 연결해 드릴게요!\n아래 버튼을 눌러주세요 😊",
    offerConsult: true,
  },
  {
    keywords: ["전화", "통화", "번호"],
    answer: "전화 상담 번호는 010-9793-9490 입니다.\n연중무휴 10:00~18:00 운영 중이에요!",
    offerConsult: true,
  },
];

const FALLBACK_ANSWERS = [
  "도어락 설치·제품·AS 관련 무엇이든 물어보세요!\n더 자세한 상담이 필요하시면 아래에서 편한 방법을 선택해 주세요 😊",
  "죄송해요, 정확한 답변이 어렵네요.\n전문 상담사와 직접 연결해 드릴까요?",
];
let fallbackIdx = 0;

const getAIReply = (text: string): { answer: string; offerConsult?: boolean } => {
  const lower = text.toLowerCase();
  for (const qa of AI_QA) {
    if (qa.keywords.some((kw) => lower.includes(kw))) {
      return { answer: qa.answer, offerConsult: qa.offerConsult };
    }
  }
  const answer = FALLBACK_ANSWERS[fallbackIdx % FALLBACK_ANSWERS.length];
  fallbackIdx += 1;
  return { answer, offerConsult: true };
};

const FloatingWidgets = () => {
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const handler = () => {
      setChatOpen(true);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    };
    window.addEventListener("doerjun:openChat", handler);
    return () => window.removeEventListener("doerjun:openChat", handler);
  }, []);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "init",
      from: "bot",
      text: "안녕하세요! 도어준 AI 상담봇입니다 😊\n설치·제품·AS·가격 등 무엇이든 물어보세요!\n\n전문 상담이 필요하시면 언제든지 연결해 드릴게요.",
      time: getTime(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, chatOpen, isTyping]);

  const sendMessage = (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      from: "user",
      text,
      time: getTime(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    setTimeout(() => {
      const { answer, offerConsult } = getAIReply(text);
      const botMsg: ChatMessage = {
        id: `b-${Date.now()}`,
        from: "bot",
        text: answer,
        time: getTime(),
        showConsultOptions: offerConsult,
      };
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 900 + Math.random() * 500);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") sendMessage();
  };

  const unreadCount = messages.filter((m) => m.from === "bot").length;

  return (
    <div className="fixed top-24 right-5 z-50 flex flex-col gap-3 items-end">
      {/* 카카오톡 오픈채팅 버튼 */}
      <a
        href="https://open.kakao.com/o/srLtta1b"
        target="_blank"
        rel="nofollow noreferrer"
        className="group flex items-center gap-2 cursor-pointer"
        title="카카오톡 오픈채팅"
      >
        <span className="hidden group-hover:flex items-center bg-white border border-stone-200 text-stone-700 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap transition-all">
          카카오 오픈채팅
        </span>
        <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: "#FEE500" }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 3C7.03 3 3 6.36 3 10.5c0 2.7 1.7 5.07 4.26 6.44l-.9 3.3a.3.3 0 0 0 .46.32L10.8 18.1A10.6 10.6 0 0 0 12 18c4.97 0 9-3.36 9-7.5S16.97 3 12 3z" fill="#3A1D1D"/>
          </svg>
        </div>
      </a>

      {/* AI 채팅 버튼 */}
      <div className="group flex items-center gap-2 cursor-pointer" onClick={() => setChatOpen((v) => !v)}>
        <span className="hidden group-hover:flex items-center bg-white border border-stone-200 text-stone-700 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap transition-all">
          AI 상담
        </span>
        <div className="w-12 h-12 rounded-full bg-stone-900 flex items-center justify-center shadow-md relative">
          <i className="ri-robot-2-line text-white text-xl" />
          {!chatOpen && unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold">
              {unreadCount}
            </span>
          )}
        </div>
      </div>

      {/* 채팅 팝업 */}
      {chatOpen && (
        <div
          className="absolute top-0 right-14 w-80 bg-white rounded-2xl border border-stone-200 overflow-hidden flex flex-col"
          style={{ height: "460px" }}
        >
          {/* 헤더 */}
          <div className="bg-stone-900 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-stone-700 flex items-center justify-center">
                <i className="ri-robot-2-line text-white text-sm" />
              </div>
              <div>
                <p className="text-white text-sm font-bold">도어준 AI 상담</p>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                  <span className="text-stone-400 text-xs">AI 자동 응답 중</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setChatOpen(false)}
              className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* 메시지 영역 */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-stone-50">
            {messages.map((msg) => (
              <div key={msg.id}>
                <div className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"} gap-2`}>
                  {msg.from === "bot" && (
                    <div className="w-7 h-7 rounded-full bg-stone-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-robot-2-line text-white text-xs" />
                    </div>
                  )}
                  <div className={`max-w-[78%] ${msg.from === "user" ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                    <div
                      className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-line leading-relaxed ${
                        msg.from === "user"
                          ? "bg-stone-900 text-white rounded-tr-sm"
                          : "bg-white text-stone-800 border border-stone-200 rounded-tl-sm"
                      }`}
                    >
                      {msg.text}
                    </div>
                    <span className="text-[10px] text-stone-400">{msg.time}</span>
                  </div>
                </div>

                {/* 상담 선택 버튼 */}
                {msg.from === "bot" && msg.showConsultOptions && (
                  <div className="mt-2 ml-9 flex flex-col gap-2">
                    <p className="text-[10px] text-stone-400 font-semibold mb-0.5">상담 방법 선택</p>
                    <a
                      href="tel:01097939490"
                      className="flex items-center gap-2 bg-stone-900 text-white text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-phone-fill text-sm" />
                      </div>
                      전화 상담 · 010-9793-9490
                    </a>
                    <a
                      href="https://open.kakao.com/o/srLtta1b"
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                      style={{ backgroundColor: "#FEE500", color: "#3A1D1D" }}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-chat-smile-2-line text-sm" />
                      </div>
                      카카오톡 오픈채팅 상담
                    </a>
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start gap-2">
                <div className="w-7 h-7 rounded-full bg-stone-900 flex items-center justify-center flex-shrink-0">
                  <i className="ri-robot-2-line text-white text-xs" />
                </div>
                <div className="bg-white border border-stone-200 rounded-2xl rounded-tl-sm px-4 py-2.5 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* 빠른 질문 */}
          <div className="px-3 pt-2 pb-1 border-t border-stone-100 bg-white flex gap-1.5 overflow-x-auto flex-shrink-0">
            {["설치 문의", "가격 견적", "AS 신청"].map((q) => (
              <button
                key={q}
                onClick={() => sendMessage(q)}
                className="flex-shrink-0 text-[11px] font-semibold text-stone-600 border border-stone-200 px-2.5 py-1 rounded-full hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {q}
              </button>
            ))}
          </div>

          {/* 입력창 */}
          <div className="px-3 py-2.5 bg-white flex items-center gap-2 flex-shrink-0">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="메시지를 입력하세요..."
              className="flex-1 text-sm px-3 py-2 rounded-xl border border-stone-200 outline-none focus:border-stone-400 transition-colors"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim()}
              className="w-9 h-9 flex items-center justify-center bg-stone-900 rounded-xl text-white disabled:opacity-40 cursor-pointer transition-opacity"
            >
              <i className="ri-send-plane-fill text-sm" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingWidgets;
